import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import type { CheckIn, CreateCheckInInput } from '../types';
import { toast } from '@/shared/lib/toast';

/**
 * Hook para buscar check-ins
 */
export function useCheckIns(filters?: {
  employeeId?: string;
  month?: number;
  year?: number;
}) {
  return useQuery({
    queryKey: ['feedback-pdi', 'check-ins', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.employeeId) params.append('employeeId', filters.employeeId);
      if (filters?.month) params.append('month', filters.month.toString());
      if (filters?.year) params.append('year', filters.year.toString());

      const response = await api.get<{ checkIns: CheckIn[]; total: number }>(
        `/feedback-pdi/check-ins?${params.toString()}`
      );

      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: false,
  });
}

/**
 * Hook para buscar um check-in específico
 */
export function useCheckIn(id?: string) {
  return useQuery({
    queryKey: ['feedback-pdi', 'check-ins', id],
    queryFn: async () => {
      if (!id) return null;

      const response = await api.get<{ checkIn: CheckIn }>(`/feedback-pdi/check-ins/${id}`);

      return response.data.checkIn;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Hook para criar check-in
 */
export function useCreateCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCheckInInput) => {
      const response = await api.post<{ checkIn: CheckIn }>('/feedback-pdi/check-ins', data);
      return response.data.checkIn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'check-ins'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'dashboard'] });
      toast.success('Check-in criado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao criar check-in');
    },
  });
}

/**
 * Hook para atualizar check-in
 */
export function useUpdateCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateCheckInInput> }) => {
      const response = await api.put<{ checkIn: CheckIn }>(`/feedback-pdi/check-ins/${id}`, data);
      return response.data.checkIn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'check-ins'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'dashboard'] });
      toast.success('Check-in atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao atualizar check-in');
    },
  });
}

/**
 * Hook para atualizar acordo
 */
export function useUpdateAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isCompleted }: { id: string; isCompleted: boolean }) => {
      const response = await api.put(`/feedback-pdi/agreements/${id}`, { isCompleted });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'check-ins'] });
      toast.success('Acordo atualizado!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao atualizar acordo');
    },
  });
}
